import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { useTheme } from 'styled-components';
import { usePortal } from '../../../hooks';
import { useOnKeyDown } from '../../../hooks/useOnKeyPress';
import { MobileModalSheet } from './MobileModalSheet';
import { MobileModalBackdropWrapper } from './styles';

export const MOBILE_MODAL_CONTENT_PORTAL = 'mobile-modal-content-portal';

export interface ModalConfig {
  content?: ReactNode;
  title?: string;
  useSheetContent?: boolean;
  /** Opens a smaller sheet anchored to the bottom, optionally on top of a previous full-height sheet. */
  bottomSheet?: boolean;
  bottomSheetHeight?: number | string;
  onCloseModal?: () => void;
  onOpenModal?: () => void;
}

interface ModalContextValue {
  openModal: (params?: ModalConfig) => void;
  closeModal: () => void;
  updateModal: (params: Partial<ModalConfig>) => void;
  pushModal: (params?: ModalConfig) => void;
  popModal: () => void;
  isModalOpen: boolean;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

const DEFAULT_CONFIG: ModalConfig = {};
interface ModalConfigState extends ModalConfig {
  modalConfigId: number;
}

interface ModalState {
  config?: ModalConfigState;
  configStack: ModalConfigState[];
  isModalOpen: boolean;
}

/**
 * Hook to open modals that are not tied to a specific component.
 * @returns The modal context, which can be used to manage a global modal.
 */
export const useMobileModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('Missing MobileModalProvider further up in the tree. Did you forget to add it?');
  }
  return context;
};

export const MobileModalProvider = function MobileModalProvider({ children }: PropsWithChildren) {
  const [modalState, setModalState] = useState<ModalState>({ configStack: [], isModalOpen: false });
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
  const nextModalConfigId = useRef(0);
  const modalStateRef = useRef(modalState);
  modalStateRef.current = modalState;
  const theme = useTheme();
  const { setPortalRef: setContentPortalRef } = usePortal<HTMLDivElement>(MOBILE_MODAL_CONTENT_PORTAL);

  const createModalConfigState = useCallback((params?: ModalConfig): ModalConfigState => {
    const modalConfigId = nextModalConfigId.current;
    nextModalConfigId.current += 1;
    return { ...DEFAULT_CONFIG, ...params, modalConfigId };
  }, []);

  const openModal = useCallback(
    (params?: ModalConfig) => {
      const newConfig = createModalConfigState(params);
      setIsBottomSheetExpanded(false);
      setModalState({ config: newConfig, configStack: [], isModalOpen: true });
      newConfig?.onOpenModal?.();
      if (typeof document !== 'undefined') {
        document.body.style.backgroundColor = theme.backgroundBody;
      }
    },
    [createModalConfigState, theme]
  );

  const updateModal = useCallback((params: Partial<ModalConfig>) => {
    setModalState(prev => (prev.config ? { ...prev, config: { ...prev.config, ...params } } : prev));
  }, []);

  const closeModal = useCallback(() => {
    const onCloseModal = modalStateRef.current.config?.onCloseModal;
    setIsBottomSheetExpanded(false);
    setModalState({ configStack: [], isModalOpen: false });
    onCloseModal?.();
    if (typeof document !== 'undefined') {
      document.body.style.backgroundColor = theme.backgroundContent;
    }
  }, [theme]);

  const pushModal = useCallback(
    (params?: ModalConfig) => {
      const newConfig = createModalConfigState(params);
      setIsBottomSheetExpanded(false);
      setModalState(prev => ({
        config: newConfig,
        configStack: prev.config != null ? [...prev.configStack, prev.config] : prev.configStack,
        isModalOpen: true,
      }));
      newConfig?.onOpenModal?.();
      if (typeof document !== 'undefined') {
        document.body.style.backgroundColor = theme.backgroundBody;
      }
    },
    [createModalConfigState, theme]
  );

  const popModal = useCallback(() => {
    // Read from a ref so callers that captured an older popModal (e.g. back buttons created at push time)
    // still see the current stack and do not reset the body background while the sheet stays open.
    const { config, configStack } = modalStateRef.current;
    config?.onCloseModal?.();
    setIsBottomSheetExpanded(false);
    const shouldCloseSheet = configStack.length === 0;
    setModalState(prev => {
      if (prev.configStack.length > 0) {
        return {
          config: prev.configStack[prev.configStack.length - 1],
          configStack: prev.configStack.slice(0, -1),
          isModalOpen: true,
        };
      }

      return { configStack: [], isModalOpen: false };
    });
    if (shouldCloseSheet && typeof document !== 'undefined') {
      document.body.style.backgroundColor = theme.backgroundContent;
    }
  }, [theme]);

  const closeCurrentModal = useCallback(() => {
    const { config, configStack } = modalStateRef.current;
    if (config?.bottomSheet === true && configStack.length > 0) {
      popModal();
      return;
    }

    closeModal();
  }, [closeModal, popModal]);

  useOnKeyDown({ key: 'Escape' }, closeCurrentModal);

  const contextValue: ModalContextValue = {
    isModalOpen: modalState.isModalOpen,
    openModal,
    closeModal,
    updateModal,
    pushModal,
    popModal,
  };

  const overlayConfig = modalState.config?.bottomSheet === true ? modalState.config : undefined;
  const backgroundConfig = overlayConfig != null ? findLastFullHeightConfig(modalState.configStack) : modalState.config;
  const showBackgroundSheet = backgroundConfig != null || overlayConfig == null;
  const overlayClosesToPrevious = overlayConfig != null && modalState.configStack.length > 0;

  return (
    <ModalContext.Provider value={contextValue}>
      <MobileModalBackdropWrapper
        isOpen={modalState.isModalOpen}
        transformContent={modalState.isModalOpen && (overlayConfig == null || isBottomSheetExpanded)}
        data-testid="mobile-modal-backdrop-wrapper"
      >
        {children}
      </MobileModalBackdropWrapper>
      {showBackgroundSheet && (
        <MobileModalSheet
          isOpen={modalState.isModalOpen && backgroundConfig != null}
          onClose={closeCurrentModal}
          onBack={
            overlayConfig == null && modalState.configStack.length > 0 && backgroundConfig?.useSheetContent === true
              ? popModal
              : undefined
          }
          title={backgroundConfig?.title}
          useSheetContent={backgroundConfig?.useSheetContent}
        >
          {backgroundConfig?.content}
          {overlayConfig == null && <div ref={setContentPortalRef} data-testid={MOBILE_MODAL_CONTENT_PORTAL} />}
        </MobileModalSheet>
      )}
      {overlayConfig != null && (
        <MobileModalSheet
          key={overlayConfig.modalConfigId}
          isOpen={modalState.isModalOpen}
          bottomSheet
          bottomSheetHeight={overlayConfig.bottomSheetHeight}
          onClose={overlayClosesToPrevious ? popModal : closeCurrentModal}
          title={overlayConfig.title}
          useSheetContent={overlayConfig.useSheetContent}
          onBottomSheetExpandedChange={setIsBottomSheetExpanded}
        >
          {overlayConfig.content}
          <div ref={setContentPortalRef} data-testid={MOBILE_MODAL_CONTENT_PORTAL} />
        </MobileModalSheet>
      )}
    </ModalContext.Provider>
  );
};

function findLastFullHeightConfig(stack: ModalConfigState[]): ModalConfigState | undefined {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].bottomSheet !== true) {
      return stack[i];
    }
  }
  return undefined;
}
